// src/encounter/order-dialog-shell.tsx
// The SHARED chrome behind every A/P order dialog (PROD-1801).
//
// Source of truth — the LIVE Flutter app, captured 2026-07-30 (dev-provider.scrips.com, test
// patient), and Figma Encounter-2 `1736:159053` (`appointments@patient@ap@edit-radiology`). Labs,
// radiology and procedure are literally THE SAME DIALOG in Flutter: identical anatomy, only the field
// label differs (LABS / RADIOLOGY / PROCEDURE) plus procedure's static coding-system label. That is
// why this is one component rather than three near-copies — three of the five order dialogs are this
// shell with a different body slot.
//
// Anatomy, top to bottom, verbatim from the frame:
//   1. READ-ONLY header — field label (12px bold uppercase, hint) + item name (15px) + code (13px
//      muted) — plus an optional right-aligned aside. In Figma the header's `Field/Freeform` and
//      `Cursor` layers are hidden="true", i.e. this is the FILLED state: the study is already chosen
//      and is NOT re-searchable here. Samer's "procedure should not be changeable in this screen"
//      is that same observation.
//   2. `DIAGNOSIS (n) *` multi-select (DiagnosisMultiSelect).
//   3. Per-type body slot — lab sub-tests, procedure billing, nothing for imaging.
//   4. `NOTE TO LAB / PATIENT` — a recessed platter with placeholder and a live `0/300` counter.
//   5. Save (filled pill) + Cancel (grey pill).
//
// The 0/300 counter is not new code — the DS TextArea renders one whenever `maxLength` is set. The
// dialogs simply never passed it, so the clinician had no warning before the note was truncated
// server-side. This shell passes it.
import type { ReactNode } from 'react';
import { Modal, Button, TextArea, TEXT_ROLE_CLASSES } from '@scripsteam/scrips-signal-ds';
import { Trash2 } from 'lucide-react';
import { useDesignV2 } from './lib/design-v2';
import { DiagnosisMultiSelect, type DiagnosisOption } from './diagnosis-multi-select';
import { AP_DIALOG_LABELS } from './ap-dialog-label-style';

export interface OrderDialogShellProps {
  /** Uppercase field label above the item name — LABS · RADIOLOGY · PROCEDURE. */
  fieldLabel: string;
  /** The chosen item's display name. Read-only here by design (see the header note above). */
  itemName: string;
  /** The item's code, shown muted beneath the name. */
  itemCode?: string;
  /**
   * Right-aligned aside on the header row. Procedure passes its coding system as a STATIC LABEL here
   * — Flutter renders `CPT` as plain text, not a dropdown (Samer, 2026-07-29: "no need for cpt as
   * dropdown"). Do not put a control in this slot.
   */
  headerAside?: ReactNode;

  /** The encounter's diagnoses + the current links. At least one link is required to save. */
  diagnoses: DiagnosisOption[];
  selectedDxIds: readonly string[];
  onDxChange: (ids: string[]) => void;

  /** Per-type body, rendered between the diagnosis block and the note. */
  children?: ReactNode;

  note: string;
  onNoteChange: (note: string) => void;
  /** Defaults to the frame's copy. */
  noteLabel?: string;
  notePlaceholder?: string;
  /** Frame shows 0/300. */
  noteMaxLength?: number;

  title: string;
  saveLabel?: string;
  /**
   * Extra save gating on top of the built-in "at least one diagnosis" rule — e.g. a lab with no
   * study chosen. The diagnosis rule is enforced HERE so no dialog can forget it.
   */
  canSave?: boolean;
  onSave: () => void;
  onCancel: () => void;
  /**
   * Destructive DELETE, bottom-right — the red trash glyph both Figma frames carry
   * (`1736:159053` radiology, `1736:158994` medication). Only meaningful when EDITING an existing
   * order, so hosts pass it only then; omitted, nothing renders.
   *
   * It is a second route to a capability the canvas already has (the order row's own remove), not new
   * behaviour — which is why it is safe to wire: the host hands over its existing removal callback.
   */
  onDelete?: () => void;
  /** Rendered above the header — the search field, on surfaces that still allow re-picking. */
  searchSlot?: ReactNode;
  /**
   * Paint `title` as a visible heading. OFF by default: the order dialogs carry no title (Andrew,
   * 30 Jul). The ADD LAB popup alone turns it on (Andrew's ruling on the lab design, PROD-2640).
   */
  showTitle?: boolean;
  /** Rendered between the header and the DIAGNOSIS block — the lab popup's specimen chip row. */
  subHeader?: ReactNode;
  /** The DIAGNOSIS block's variant. `'select-all'` is the lab popup's; everyone else keeps the default. */
  diagnosisVariant?: 'default' | 'select-all';
  /**
   * A search that found nothing (the Add lab popup's edge state, canvas artboard 9 "No results"):
   * when set, the dialog shows the search, this box, and `itemName` as one muted line, then the
   * actions. The header, DIAGNOSIS, body and note are not shown: there is no item to order yet.
   */
  emptySlot?: ReactNode;
  /**
   * AUTHORING an order set (PROD-2714, Settings › Order sets): no encounter, so no DIAGNOSIS block
   * (a set carries its own diagnoses) and no note (the Save-as board's "Not kept: ... notes to the
   * lab"). The at-least-one-diagnosis rule does not apply: there is nothing to link to.
   */
  authoring?: boolean;
}

const BODY = 'font-sans flex flex-col gap-6';
// The lab popup's visible title: 17px semibold, 8px above the body (canvas AddLab* artboards).
// The no-results state's "No lab selected" line: 13px muted.
const EMPTY_ITEM = 'text-size-sm leading-reading text-text-muted';
const TITLE = 'm-0 mb-2 font-sans text-size-title font-semibold text-text-primary';
/** Design sheet v1.1 §1 Type (switch ON only): the DS has no 17px step — a dialog title is the DS
 *  `panel-title` role (18 / semibold, text-primary). */
const TITLE_V2 = `m-0 mb-2 font-sans ${TEXT_ROLE_CLASSES['panel-title']}`;
// The shared treatment — same values the scoped CSS rule (ap-dialog-label-style.tsx) uses, so the
// header's field label and the DS-rendered field labels below it can never drift apart. That file is
// out of scope for this pass, so its literal AP_FIELD_LABEL values (12/700/uppercase/0.01em/
// --color-text-hint) are reproduced here as the equivalent class string rather than imported as a
// style object.
const FIELD_LABEL =
  'block text-size-eyebrow font-bold uppercase tracking-field-label text-text-hint';
const HEADER_ROW = 'flex items-start justify-between gap-3';
// fontSize 15 -> --text-body-15; lineHeight 1.432 has no named DS leading token (LIST — arbitrary).
const ITEM_NAME = 'text-size-body-15 leading-reading text-text-primary mt-2';
const ITEM_CODE = 'text-size-sm leading-reading text-text-muted';
// Save + Cancel LEFT, delete pushed to the far RIGHT — the layout of both frames' footer rows.
// FIXED FOOTER, as Flutter (Andrew, 1 Aug: "the user should not scroll to the end to close or save").
// base_ap_item_add_widget.dart:88 wraps the body in `Expanded(child: SingleChildScrollView(...))` and
// leaves the actions OUTSIDE the scroll area, so Save/Cancel are always reachable however long the
// form runs. Sticky rather than absolutely positioned: the row keeps its place in flow, so it cannot
// overlap the last field the way a fixed overlay would.
const ACTIONS =
  'sticky bottom-0 bg-surface-card pt-3 pb-1 border-t border-solid border-t-border-subtle z-1 flex items-center justify-between gap-2 mt-2';
const ACTIONS_MAIN = 'flex items-center gap-2';
// The frames draw a bare red trash glyph, not a filled destructive pill. `IconButton` is the DS's
// icon-only atom; the error tone comes from the token, and the quiet (non-elevated) variant is the
// closest DS look. A quiet DESTRUCTIVE icon action is a real DS gap — flagged in the PR rather than
// hand-rolled into a bespoke button here.
/** The app icon button: a destructive icon is neutral, like every row bin (icon pass, 16 Oct; it was red at rest). */
const DELETE_BUTTON = 'icon-action';

export function OrderDialogShell({
  fieldLabel,
  itemName,
  itemCode,
  headerAside,
  diagnoses,
  selectedDxIds,
  onDxChange,
  children,
  note,
  onNoteChange,
  noteLabel = 'Note to lab / patient',
  notePlaceholder = 'Enter notes',
  noteMaxLength = 300,
  title,
  saveLabel = 'Save',
  canSave = true,
  onSave,
  onCancel,
  onDelete,
  searchSlot,
  showTitle = false,
  subHeader,
  diagnosisVariant = 'default',
  emptySlot,
  authoring = false,
}: OrderDialogShellProps) {
  const designV2 = useDesignV2();
  // At least one diagnosis link is REQUIRED — that is what the error-toned asterisk means, and
  // Flutter blocks the save with "No diagnosis is selected." Enforced in the shell so no individual
  // dialog can forget it. It is also load-bearing for persistence: an order with no dxIds is dropped
  // by the save mapper, so allowing the save would silently discard the order.
  const hasDx = authoring || selectedDxIds.length > 0;
  const saveEnabled = canSave && hasDx;

  return (
    // `lg` = 640px. The Figma card is 518px, which is not a DS Modal size (sm 364 / md 480 / lg 640);
    // md would clip the two-column diagnosis grid, so lg is the honest choice. Flagged rather than
    // hand-rolling a width — a 518 size belongs in the DS if this recurs.
    // NO MODAL TITLE (Andrew, 30 Jul): neither Figma frame has one — the card opens straight
    // onto the field label ("RADIOLOGY" / "MEDICATION"). "Add imaging" was drift I introduced.
    // `title` is kept on the props as the dialog's ACCESSIBLE name, so screen-reader users still
    // hear what opened; it is simply not painted.
    // ONE EXCEPTION: the Add lab popup passes `showTitle` (Andrew's ruling on the lab design,
    // PROD-2640). The default stays off, so no other dialog changes.
    <Modal open onClose={onCancel} size="lg">
      {showTitle && (
        <h2 data-slot="order-dialog-title" className={designV2 ? TITLE_V2 : TITLE}>
          {title}
        </h2>
      )}
      {/* ONE uppercase field-label treatment across every A&P dialog — this covers labs, radiology,
          procedure and the shell's own note field. See ap-dialog-label-style for why uppercase is the
          target (Flutter + NumberSelector already use it; the DS-labelled controls were the drift). */}
      <div
        role="group"
        aria-label={title}
        data-slot="order-dialog-body"
        className={`${BODY} ${AP_DIALOG_LABELS}`}
      >
        {searchSlot}

        {emptySlot ? (
          <>
            {emptySlot}
            <div className={EMPTY_ITEM}>{itemName}</div>
          </>
        ) : (
          <>
            <div>
              <div className={HEADER_ROW}>
                <span data-slot="order-dialog-field-label" className={FIELD_LABEL}>
                  {fieldLabel}
                </span>
                {headerAside}
              </div>
              <div data-slot="order-dialog-item-name" className={ITEM_NAME}>
                {itemName}
              </div>
              {itemCode && (
                <div data-slot="order-dialog-item-code" className={ITEM_CODE}>
                  {itemCode}
                </div>
              )}
            </div>

            {subHeader}

            {!authoring && (
              <DiagnosisMultiSelect
                diagnoses={diagnoses}
                selectedIds={selectedDxIds}
                onChange={onDxChange}
                variant={diagnosisVariant}
              />
            )}

            {children}

            {/*
              The `0/300` counter in the frame is the DS TextArea's OWN — it renders one whenever
              `maxLength` is set. The dialogs simply never passed `maxLength`, so the clinician got no
              warning before the note was truncated server-side. Passing it is the whole fix; do NOT add
              a second counter alongside (an earlier draft here did, and the duplicate is what the tests
              caught).
            */}
            {!authoring && (
              <TextArea
                label={noteLabel}
                value={note}
                onChange={(e) => onNoteChange(e.target.value.slice(0, noteMaxLength))}
                placeholder={notePlaceholder}
                rows={3}
                maxLength={noteMaxLength}
              />
            )}
          </>
        )}

        {/* Save LEFT of Cancel and the pair LEFT-aligned, per the frame — not the usual right-aligned
            pair — with the destructive delete opposite them. */}
        <div data-slot="order-dialog-actions" className={ACTIONS}>
          <div className={ACTIONS_MAIN}>
            <Button size="sm" disabled={!saveEnabled} onClick={onSave}>
              {saveLabel}
            </Button>
            <Button variant="tertiary" size="sm" onClick={onCancel}>
              Cancel
            </Button>
          </div>
          {onDelete && (
            <button
              type="button"
              aria-label="Delete order"
              data-slot="order-dialog-delete"
              className={DELETE_BUTTON}
              onClick={onDelete}
            >
              <Trash2 className="size-4" aria-hidden />
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
